/**
 * client/ui/account.ts
 * Responsabilidad: Gestión del Modal de Cuenta, Autenticación (Login / Registro),
 * visualización de Mi Perfil y Tablas de Clasificación Global (Top 10).
 */

import type { Client } from "@colyseus/sdk";
import Swal from "sweetalert2";
import { profileManager, formatTime } from "../profile";
import { sanitizeCosmetics, DEFAULT_COSMETICS } from "../cosmetics";
import { safeGetStorage, safeSetStorage, clearUserSessionStorage } from "../utils";
import { fetchGlobalLeaderboard, renderGlobalLeaderboardTable } from "./leaderboards";
import { t } from "../i18n";
import { questManager } from "../quests";
import { RouletteUI } from "./roulette";
import { emoteManager } from "../emotes";

export type AccountTab = "login" | "profile" | "ranking" | "roulette";

export interface AccountManagerOptions {
    onOpen?: () => void;
    onClose?: () => void;
}

export class AccountManager {
    private authClient?: Client;
    private options: AccountManagerOptions;
    private modalOpenedAt: number = 0;
    private activeTab: AccountTab = "login";

    // Modal base DOM
    private accountModal = document.getElementById("account-modal");
    private accountClose = document.getElementById("account-close");
    private accountBtn = document.getElementById("account-btn") || document.getElementById("profile-btn");

    // Tab buttons
    private tabButtons = document.querySelectorAll<HTMLButtonElement>(".account-tab-btn");
    private tabPaneLogin = document.getElementById("tab-pane-login");
    private tabPaneProfile = document.getElementById("tab-pane-profile");
    private tabPaneRanking = document.getElementById("tab-pane-ranking");
    private tabPaneRoulette = document.getElementById("tab-pane-roulette");
    private rouletteUI?: RouletteUI;

    // Auth DOM
    private loggedOutView = document.getElementById("account-logged-out-view");
    private loggedInView = document.getElementById("account-logged-in-view");
    private authModeLoginBtn = document.getElementById("auth-mode-login-btn") as HTMLButtonElement | null;
    private authModeRegisterBtn = document.getElementById("auth-mode-register-btn") as HTMLButtonElement | null;
    private authFormLogin = document.getElementById("auth-form-login");
    private authFormRegister = document.getElementById("auth-form-register");
    private switchToRegisterLink = document.getElementById("switch-to-register");
    private switchToLoginLink = document.getElementById("switch-to-login");
    private emailInput = document.getElementById("account-email-input") as HTMLInputElement | null;
    private passwordInput = document.getElementById("account-password-input") as HTMLInputElement | null;
    private registerEmailInput = document.getElementById("account-register-email-input") as HTMLInputElement | null;
    private registerPasswordInput = document.getElementById("account-register-password-input") as HTMLInputElement | null;
    private confirmPasswordGroup = document.getElementById("confirm-password-group");
    private confirmPasswordInput = document.getElementById("account-confirm-password-input") as HTMLInputElement | null;
    private countryGroup = document.getElementById("account-country-group");
    private countrySelect = document.getElementById("account-country-select") as HTMLSelectElement | null;
    private forgotPasswordLink = document.getElementById("forgot-password-link");
    private loginBtn = document.getElementById("account-login-btn");
    private registerBtn = document.getElementById("account-register-btn");
    private logoutBtn = document.getElementById("account-logout-btn");
    private userEmailDisplay = document.getElementById("account-user-email");
    private authFeedback = document.getElementById("account-feedback");

    // Profile DOM
    private profileAliasValue = document.getElementById("account-profile-alias-value");
    private sessionBadge = document.getElementById("account-session-badge");
    private statGames = document.getElementById("account-stat-games");
    private statTime = document.getElementById("account-stat-time");
    private statKills = document.getElementById("account-stat-kills");
    private statBestMass = document.getElementById("account-stat-best-mass");
    private statStreak = document.getElementById("account-stat-streak");
    private statBestStreak = document.getElementById("account-stat-best-streak");
    private statBestRank = document.getElementById("account-stat-best-rank");
    private statFavorite = document.getElementById("account-stat-favorite");
    private statCountry = document.getElementById("account-stat-country");

    // Ranking DOM
    private leaderboardRefreshBtn = document.getElementById("account-leaderboard-refresh");
    private leaderboardLoading = document.getElementById("account-leaderboard-loading");
    private killsTbody = document.getElementById("account-leaderboard-kills-tbody");
    private massTbody = document.getElementById("account-leaderboard-mass-tbody");
    private streakTbody = document.getElementById("account-leaderboard-streak-tbody");
    private timeTbody = document.getElementById("account-leaderboard-time-tbody");

    constructor(authClient?: Client, options: AccountManagerOptions = {}) {
        this.authClient = authClient;
        this.options = options;

        this.initTabs();
        this.initAuthListeners();
        this.initModalControls();
        this.initLeaderboardControls();
        this.rouletteUI = new RouletteUI();

        void this.checkAuthState();
    }

    public isOpen(): boolean {
        return Boolean(this.accountModal && !this.accountModal.classList.contains("hidden"));
    }

    public open(tab?: AccountTab): void {
        if (tab === "roulette") {
            if (!this.rouletteUI) {
                this.rouletteUI = new RouletteUI();
            }
            this.rouletteUI.open();
            return;
        }

        if (!this.accountModal) return;

        this.switchTab("login");

        this.accountModal.classList.remove("hidden");
        this.modalOpenedAt = performance.now();

        if (this.options.onOpen) {
            this.options.onOpen();
        }
    }

    public close(): void {
        if (this.accountModal) {
            this.accountModal.classList.add("hidden");
        }
        if (this.passwordInput) {
            this.passwordInput.value = "";
        }
        if (this.registerPasswordInput) {
            this.registerPasswordInput.value = "";
        }
        if (this.confirmPasswordInput) {
            this.confirmPasswordInput.value = "";
        }
        if (this.countrySelect) {
            this.countrySelect.value = "";
        }
        const registerAliasInput = document.getElementById("account-register-alias-input") as HTMLInputElement | null;
        if (registerAliasInput) {
            registerAliasInput.value = "";
        }
        this.clearFeedback();
        if (this.options.onClose) {
            this.options.onClose();
        }
    }

    public toggle(tab?: AccountTab): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open(tab);
        }
    }

    public switchTab(tab: AccountTab): void {
        if (tab === "roulette") {
            if (!this.rouletteUI) {
                this.rouletteUI = new RouletteUI();
            }
            this.rouletteUI.open();
            return;
        }

        this.activeTab = "login";

        this.tabButtons.forEach((btn) => {
            const btnTab = btn.dataset.tab;
            btn.classList.toggle("active", btnTab === tab);
        });

        if (this.tabPaneLogin) this.tabPaneLogin.classList.remove("hidden");
        if (this.tabPaneProfile) this.tabPaneProfile.classList.toggle("hidden", tab !== "profile");
        if (this.tabPaneRanking) this.tabPaneRanking.classList.toggle("hidden", tab !== "ranking");
        if (this.tabPaneRoulette) this.tabPaneRoulette.classList.add("hidden");

        void this.checkAuthState();
    }

    public async checkAuthState(): Promise<boolean> {
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");

        if (this.authClient && token) {
            try {
                const userData = await this.authClient.auth.getUserData();
                if (userData && userData.user && userData.user.email) {
                    this.renderAuthState(userData.user.email);
                    return true;
                }
            } catch {
                // Token inválido o expirado
            }
        }

        this.renderAuthState(null);
        return false;
    }

    public renderAuthState(email: string | null): void {
        const welcomeBanner = document.getElementById("welcome-bonus-banner");
        if (email) {
            welcomeBanner?.classList.add("hidden");
            if (this.loggedOutView) {
                this.loggedOutView.classList.add("hidden");
                this.loggedOutView.style.display = "none";
            }
            if (this.loggedInView) {
                this.loggedInView.classList.remove("hidden");
                this.loggedInView.style.display = "flex";
            }
            if (this.userEmailDisplay) this.userEmailDisplay.textContent = email;
            if (this.sessionBadge) {
                this.sessionBadge.textContent = "● Sincronizado en Nube";
                this.sessionBadge.className = "account-session-badge synced";
            }
            if (this.accountBtn) {
                this.accountBtn.title = `Cuenta: ${email} (P)`;
            }
            if (this.rouletteUI) {
                void this.rouletteUI.refresh();
            }
        } else {
            welcomeBanner?.classList.remove("hidden");
            if (this.loggedOutView) {
                this.loggedOutView.classList.remove("hidden");
                this.loggedOutView.style.display = "flex";
            }
            if (this.loggedInView) {
                this.loggedInView.classList.add("hidden");
                this.loggedInView.style.display = "none";
            }
            this.switchAuthMode("login");
            if (this.passwordInput) {
                this.passwordInput.value = "";
            }
            if (this.registerPasswordInput) {
                this.registerPasswordInput.value = "";
            }
            if (this.confirmPasswordInput) {
                this.confirmPasswordInput.value = "";
            }
            if (this.countrySelect) {
                this.countrySelect.value = "";
            }
            if (this.sessionBadge) {
                this.sessionBadge.textContent = "○ Modo Invitado";
                this.sessionBadge.className = "account-session-badge guest";
            }
            if (this.accountBtn) {
                this.accountBtn.title = "Cuenta & Ranking (P)";
            }
        }
    }

    private showLoggedInState(email: string): void {
        this.renderAuthState(email);
    }

    private showLoggedOutState(): void {
        this.renderAuthState(null);
    }

    public switchAuthMode(mode: "login" | "register"): void {
        if (this.authModeLoginBtn) {
            this.authModeLoginBtn.classList.toggle("active", mode === "login");
        }
        if (this.authModeRegisterBtn) {
            this.authModeRegisterBtn.classList.toggle("active", mode === "register");
        }
        if (this.authFormLogin) {
            this.authFormLogin.classList.toggle("hidden", mode !== "login");
        }
        if (this.authFormRegister) {
            this.authFormRegister.classList.toggle("hidden", mode !== "register");
        }
        this.clearFeedback();

        if (mode === "login") {
            const regEmail = (this.registerEmailInput?.value || "").trim();
            if (regEmail && this.emailInput && !this.emailInput.value) {
                this.emailInput.value = regEmail;
            }
        } else {
            const logEmail = (this.emailInput?.value || "").trim();
            if (logEmail && this.registerEmailInput && !this.registerEmailInput.value) {
                this.registerEmailInput.value = logEmail;
            }
        }
    }

    private showFeedback(msg: string, type: "info" | "success" | "error"): void {
        if (!this.authFeedback) return;
        this.authFeedback.textContent = msg;
        this.authFeedback.className = `account-feedback ${type}`;
        this.authFeedback.classList.remove("hidden");
    }

    private showAccountFeedback(msg: string, type: "info" | "success" | "error"): void {
        this.showFeedback(msg, type);
    }

    private clearFeedback(): void {
        if (this.authFeedback) {
            this.authFeedback.textContent = "";
            this.authFeedback.className = "account-feedback hidden";
        }
    }

    private initTabs(): void {
        this.tabButtons.forEach((btn) => {
            btn.addEventListener("click", () => {
                const tab = btn.dataset.tab as AccountTab;
                if (tab) this.switchTab(tab);
            });
        });
    }

    private initAuthListeners(): void {
        this.authModeLoginBtn?.addEventListener("click", () => this.switchAuthMode("login"));
        this.authModeRegisterBtn?.addEventListener("click", () => this.switchAuthMode("register"));
        this.switchToRegisterLink?.addEventListener("click", (e) => {
            e.preventDefault();
            this.switchAuthMode("register");
        });
        this.switchToLoginLink?.addEventListener("click", (e) => {
            e.preventDefault();
            this.switchAuthMode("login");
        });

        if (this.loginBtn) {
            this.loginBtn.addEventListener("click", async () => {

                const email = (this.emailInput?.value || "").trim();
                const password = (this.passwordInput?.value || "").trim();

                if (!email || !password) {
                    this.showAccountFeedback(t("auth.fillCredentials"), "error");
                    return;
                }
                if (!this.authClient) {
                    this.showAccountFeedback("Servicio de autenticación no disponible.", "error");
                    return;
                }

                const loginBtnEl = this.loginBtn as HTMLButtonElement | null;
                const originalLoginText = loginBtnEl ? loginBtnEl.textContent || "Iniciar Sesión" : "Iniciar Sesión";
                if (loginBtnEl) {
                    loginBtnEl.disabled = true;
                    loginBtnEl.innerHTML = `<span class="spinner"></span> ${t("auth.loggingIn") || "Iniciando..."}`;
                }

                try {
                    this.showAccountFeedback(t("auth.loggingIn") || "Iniciando sesión...", "info");
                    const res = await this.authClient.auth.signInWithEmailAndPassword(email, password);
                    if (res && res.token) {
                        localStorage.setItem("alchem_auth_token", res.token);
                    }
                    this.showAccountFeedback(t("auth.loginSuccess"), "success");
                    await this.checkAuthState();

                    // Cargar perfil del usuario desde Supabase
                    const token = this.authClient.auth.token || localStorage.getItem("alchem_auth_token");
                    if (token) {
                        const profileRes = await fetch("/api/profile", {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        if (profileRes.ok) {
                            const p = await profileRes.json();
                            if (p && p.userId) {
                                profileManager.hydrate(p);
                                if (p.alias) {
                                    safeSetStorage("alchem_alias", p.alias);
                                    const aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
                                    if (aliasInput) aliasInput.value = p.alias;
                                }
                                const safeCos = p.cosmetics && Object.keys(p.cosmetics).length > 0 ? sanitizeCosmetics(p.cosmetics) : DEFAULT_COSMETICS;
                                localStorage.setItem("alchem_cosmetics", JSON.stringify(safeCos));
                                window.dispatchEvent(new CustomEvent("alchem:auth_change", { 
                                    detail: { isLoggedIn: true, alias: p.alias || "Alquimista", profile: p, cosmetics: safeCos } 
                                }));
                                void questManager.fetchServerState();

                                if (p.welcomeBonusJustGranted) {
                                    questManager.updateBalances(p.crystals, p.astralOrbs);
                                    document.getElementById("welcome-bonus-banner")?.classList.add("hidden");
                                    void Swal.fire({
                                        title: "🎉 ¡Bono de Bienvenida Acreditado!",
                                        text: "Por unirte a Alchem.io has recibido +200 💎 Cristales Alquímicos y +10 🔮 Piedras Filosofales en tu cuenta.",
                                        icon: "success",
                                        confirmButtonText: "¡Genial!",
                                        confirmButtonColor: "#f59e0b",
                                        background: "#0f172a",
                                        color: "#f8fafc",
                                    });
                                }
                            }
                        }
                    }

                    setTimeout(() => {
                        this.close();
                    }, 300);
                } catch (e: any) {
                    if (e?.message === "email_not_confirmed") {
                        const result = await Swal.fire({
                            icon: 'warning',
                            title: t("auth.emailNotConfirmedTitle") || 'Correo no confirmado',
                            text: t("auth.emailNotConfirmed"),
                            showCancelButton: true,
                            confirmButtonText: '📩 Reenviar correo de activación',
                            cancelButtonText: 'Entendido',
                            confirmButtonColor: '#38bdf8',
                            cancelButtonColor: '#64748b',
                            background: '#0f172a',
                            color: '#f8fafc',
                        });

                        if (result.isConfirmed) {
                            try {
                                Swal.fire({
                                    title: 'Reenviando correo...',
                                    allowOutsideClick: false,
                                    background: '#0f172a',
                                    color: '#f8fafc',
                                    didOpen: () => {
                                        Swal.showLoading();
                                    },
                                });

                                const res = await fetch('/api/auth/resend-confirmation', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ email }),
                                });
                                const data = await res.json();

                                if (res.ok && data.success) {
                                    await Swal.fire({
                                        icon: 'success',
                                        title: '¡Correo reenviado!',
                                        text: '¡Correo reenviado! Revisa tu bandeja de entrada o la carpeta de spam.',
                                        confirmButtonText: 'Entendido',
                                        confirmButtonColor: '#38bdf8',
                                        background: '#0f172a',
                                        color: '#f8fafc',
                                    });
                                    this.showAccountFeedback("¡Correo reenviado! Revisa tu bandeja de entrada o la carpeta de spam.", "success");
                                } else {
                                    await Swal.fire({
                                        icon: 'info',
                                        title: 'Información',
                                        text: data.message || 'No se pudo reenviar el correo.',
                                        confirmButtonText: 'Entendido',
                                        confirmButtonColor: '#38bdf8',
                                        background: '#0f172a',
                                        color: '#f8fafc',
                                    });
                                    this.showAccountFeedback(data.message || "No se pudo reenviar el correo.", "error");
                                }
                            } catch (err: any) {
                                await Swal.fire({
                                    icon: 'error',
                                    title: 'Error de conexión',
                                    text: 'No se pudo contactar con el servidor para reenviar el correo.',
                                    confirmButtonText: 'Entendido',
                                    confirmButtonColor: '#38bdf8',
                                    background: '#0f172a',
                                    color: '#f8fafc',
                                });
                                this.showAccountFeedback("Error al contactar con el servidor.", "error");
                            }
                        } else {
                            this.showAccountFeedback(t("auth.emailNotConfirmed"), "error");
                        }
                    } else if (e?.message === "invalid_credentials") {
                        await Swal.fire({
                            icon: 'error',
                            title: t("auth.invalidCredentialsTitle") || 'Credenciales inválidas',
                            text: t("auth.invalidCredentials"),
                        });
                        this.showAccountFeedback(t("auth.invalidCredentials"), "error");
                    } else {
                        this.showAccountFeedback(e?.message || "Error al iniciar sesión.", "error");
                    }
                } finally {
                    if (loginBtnEl) {
                        loginBtnEl.disabled = false;
                        loginBtnEl.textContent = originalLoginText;
                    }
                }
            });
        }

        if (this.registerBtn) {
            this.registerBtn.addEventListener("click", async () => {
                const email = (this.registerEmailInput?.value || this.emailInput?.value || "").trim();
                const password = (this.registerPasswordInput?.value || "").trim();
                const confirmPassword = (this.confirmPasswordInput?.value || "").trim();

                if (!email || !password) {
                    this.showAccountFeedback(t("auth.fillCredentials"), "error");
                    return;
                }

                if (password !== confirmPassword) {
                    this.showAccountFeedback(t("auth.passwordsDontMatch"), "error");
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: t("auth.passwordsDontMatch"),
                    });
                    return;
                }

                if (password.length < 6) {
                    this.showAccountFeedback(t("auth.passwordTooShort"), "error");
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: t("auth.passwordTooShort"),
                    });
                    return;
                }

                if (!this.authClient) {
                    this.showAccountFeedback("Servicio de autenticación no disponible.", "error");
                    return;
                }

                const registerBtnEl = this.registerBtn as HTMLButtonElement | null;
                const originalRegisterText = registerBtnEl ? registerBtnEl.textContent || "Crear Cuenta" : "Crear Cuenta";
                if (registerBtnEl) {
                    registerBtnEl.disabled = true;
                    registerBtnEl.innerHTML = `<span class="spinner"></span> ${t("auth.creatingAccount") || "Creando..."}`;
                }

                try {
                    this.showAccountFeedback(t("auth.creatingAccount") || "Creando tu cuenta de Alquimista...", "info");

                    // Obtener token fresco de Google reCAPTCHA v3
                    const recaptchaToken = await getRecaptchaToken("register");
                    const country = this.countrySelect?.value || "Unknown";
                    const alias = (document.getElementById("account-register-alias-input") as HTMLInputElement)?.value.trim() || "Anon";

                    // Enviar país, alias y token junto con las credenciales al servidor Colyseus
                    await this.authClient.auth.registerWithEmailAndPassword(email, password, { recaptchaToken, country, alias });

                    // Doble Opt-in estricto: NO guardar sesión local y desloguear SDK
                    localStorage.removeItem("alchem_auth_token");
                    localStorage.removeItem("colyseus-auth-token");
                    if (this.authClient && this.authClient.auth) {
                        (this.authClient.auth as any).token = null;
                    }

                    // Limpiar contraseñas y alias
                    if (this.registerPasswordInput) this.registerPasswordInput.value = "";
                    if (this.passwordInput) this.passwordInput.value = "";
                    if (this.confirmPasswordInput) this.confirmPasswordInput.value = "";
                    const regAliasInput = document.getElementById("account-register-alias-input") as HTMLInputElement | null;
                    if (regAliasInput) regAliasInput.value = "";

                    // Alerta obligatoria de SweetAlert2 para Doble Opt-in
                    await Swal.fire({
                        icon: 'info',
                        title: '¡Confirma tu correo electrónico!',
                        text: 'Hemos enviado un enlace de confirmación a tu correo. Por favor, confirma tu cuenta antes de iniciar sesión.',
                        confirmButtonText: 'Entendido',
                        confirmButtonColor: '#38bdf8',
                    });

                    this.showAccountFeedback("Hemos enviado un correo de confirmación. Por favor verifica tu cuenta.", "success");

                    // Conmutar automáticamente a la sub-pestaña "Iniciar Sesión" con email prellenado
                    if (this.emailInput) {
                        this.emailInput.value = email;
                    }
                    this.switchAuthMode("login");
                    // NO llamamos a checkAuthState() para que la sesión permanezca cerrada
                } catch (err: any) {
                    let msg = err?.message || "Error al crear la cuenta. Inténtalo de nuevo.";
                    if (err?.message === "invalid_email_format") {
                        msg = t("auth.invalidEmail");
                    } else if (err?.message === "disposable_email_not_allowed") {
                        msg = t("auth.disposableEmail");
                    } else if (err?.message === "recaptcha_failed") {
                        msg = t("auth.recaptchaFailed");
                    } else if (err?.message === "email_already_in_use") {
                        msg = t("auth.emailAlreadyInUse");
                    } else if (err?.message === "alias_already_taken") {
                        msg = "Ese alias ya está en uso por otro alquimista. Elige uno diferente.";
                    }

                    this.showAccountFeedback(msg, "error");
                    await Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: msg,
                    });
                } finally {
                    if (registerBtnEl) {
                        registerBtnEl.disabled = false;
                        registerBtnEl.textContent = originalRegisterText;
                    }
                }
            });
        }

        if (this.forgotPasswordLink) {
            this.forgotPasswordLink.addEventListener("click", async (e) => {
                e.preventDefault();

                const { value: email } = await Swal.fire({
                    title: "Recuperar Contraseña",
                    text: "Introduce tu correo electrónico para recibir el enlace de recuperación:",
                    input: "email",
                    inputPlaceholder: "tu@email.com",
                    showCancelButton: true,
                    confirmButtonText: "Enviar Enlace",
                    cancelButtonText: "Cancelar",
                    inputValidator: (value) => {
                        if (!value) return "Debes introducir un correo electrónico";
                        return null;
                    }
                });

                if (!email) return;

                try {
                    const authAny = this.authClient?.auth as any;
                    if (typeof authAny?.sendPasswordResetEmail === "function") {
                        await authAny.sendPasswordResetEmail(email);
                    } else if (typeof authAny?.requestPasswordReset === "function") {
                        await authAny.requestPasswordReset(email);
                    } else {
                        const resp = await fetch("/auth/forgot-password", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email })
                        });
                        if (!resp.ok) {
                            throw new Error("Error en la solicitud de recuperación.");
                        }
                    }

                    await Swal.fire({
                        icon: "success",
                        title: "Correo Enviado",
                        text: "Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.",
                        timer: 4000,
                        showConfirmButton: false,
                    });
                } catch (err) {
                    console.error("[Auth] Error al solicitar recuperación:", err);
                    Swal.fire({
                        icon: "error",
                        title: "Error",
                        text: "No se pudo solicitar la recuperación. Inténtalo de nuevo.",
                    });
                }
            });
        }

        const googleBtn = document.getElementById("google-login-btn");
        if (googleBtn) {
            googleBtn.addEventListener("click", () => this.handleOAuthLogin("google"));
        }

        const facebookBtn = document.getElementById("facebook-login-btn");
        if (facebookBtn) {
            facebookBtn.addEventListener("click", () => this.handleOAuthLogin("facebook"));
        }

        const goProfileBtn = document.getElementById("account-go-profile-btn");
        if (goProfileBtn) {
            goProfileBtn.addEventListener("click", () => {
                window.location.href = "/perfil.html";
            });
        }

        if (this.logoutBtn) {
            this.logoutBtn.addEventListener("click", async () => {
                try {
                    await this.authClient?.auth.signOut();
                } catch {
                    // Ignorar error al desconectar token remoto
                }

                clearUserSessionStorage();
                questManager.resetToGuest();
                profileManager.resetProfile();
                localStorage.setItem("alchem_cosmetics", JSON.stringify(DEFAULT_COSMETICS));

                // Recargar página para inicializar estado limpio de invitado
                window.location.reload();
            });
        }

        // Editar Perfil (SweetAlert2)
        const editProfileBtn = document.getElementById("edit-profile-btn");
        if (editProfileBtn) {
            editProfileBtn.addEventListener("click", async () => {
                const currentAlias = safeGetStorage("alchem_alias") || "Anon";
                const currentCountry = profileManager.getProfile().country || "Colombia";

                const countries = [
                    { code: "Argentina", name: "🇦🇷 Argentina" },
                    { code: "Chile", name: "🇨🇱 Chile" },
                    { code: "Colombia", name: "🇨🇴 Colombia" },
                    { code: "España", name: "🇪🇸 España" },
                    { code: "Estados Unidos", name: "🇺🇸 Estados Unidos" },
                    { code: "México", name: "🇲🇽 México" },
                    { code: "Perú", name: "🇵🇪 Perú" },
                    { code: "Otro", name: "🌐 Otro" },
                ];

                const countryOptions = countries.map(c => 
                    `<option value="${c.code}" ${c.code === currentCountry ? "selected" : ""}>${c.name}</option>`
                ).join("");

                const { value: formValues } = await Swal.fire({
                    title: 'Editar Perfil',
                    html:
                        '<div style="text-align:left; margin-bottom: 4px; font-size: 13px; color: #94a3b8;">Alias (máx 12):</div>' +
                        `<input id="swal-alias" class="swal2-input" style="margin: 0 0 16px 0; width: 100%; box-sizing: border-box;" placeholder="Alias (máx 12)" maxlength="12" value="${currentAlias}">` +
                        '<div style="text-align:left; margin-bottom: 4px; font-size: 13px; color: #94a3b8;">País:</div>' +
                        `<select id="swal-country" class="swal2-select" style="margin: 0; width: 100%; box-sizing: border-box; display: block;">${countryOptions}</select>`,
                    focusConfirm: false,
                    showCancelButton: true,
                    confirmButtonText: 'Guardar',
                    cancelButtonText: 'Cancelar',
                    preConfirm: () => {
                        const aliasVal = (document.getElementById('swal-alias') as HTMLInputElement)?.value.trim();
                        const countryVal = (document.getElementById('swal-country') as HTMLSelectElement)?.value;
                        if (!aliasVal) {
                            Swal.showValidationMessage('El alias no puede estar vacío');
                            return false;
                        }
                        return {
                            alias: aliasVal,
                            country: countryVal,
                        };
                    }
                });

                if (formValues) {
                    const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                    if (!token) {
                        safeSetStorage("alchem_alias", formValues.alias);
                        profileManager.hydrate({ country: formValues.country });
                        const aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
                        if (aliasInput) aliasInput.value = formValues.alias;
                        await this.refreshProfile();
                        Swal.fire({
                            icon: 'success',
                            title: 'Guardado',
                            text: 'Tu alias y país han sido actualizados localmente.',
                            timer: 2000,
                            showConfirmButton: false,
                        });
                        return;
                    }

                    try {
                        const res = await fetch("/api/profile", {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                            body: JSON.stringify(formValues),
                        });
                        if (res.ok) {
                            safeSetStorage("alchem_alias", formValues.alias);
                            profileManager.hydrate({ country: formValues.country });
                            const aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
                            if (aliasInput) aliasInput.value = formValues.alias;

                            await Swal.fire({
                                icon: 'success',
                                title: 'Guardado',
                                text: 'Tu perfil ha sido actualizado en la nube.',
                                timer: 2000,
                                showConfirmButton: false,
                            });
                            await this.refreshProfile();
                        } else {
                            Swal.fire('Error', 'No se pudo guardar el perfil.', 'error');
                        }
                    } catch {
                        Swal.fire('Error', 'Error de red al actualizar perfil.', 'error');
                    }
                }
            });
        }

        // Eliminar Cuenta (SweetAlert2 con advertencia)
        const deleteAccountBtn = document.getElementById("delete-account-btn");
        if (deleteAccountBtn) {
            deleteAccountBtn.addEventListener("click", async () => {
                const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                if (!token) {
                    Swal.fire('Atención', 'Estás en modo invitado. No hay una cuenta en la nube asociada para eliminar.', 'info');
                    return;
                }

                const result = await Swal.fire({
                    title: '¿Estás seguro?',
                    text: "Esta acción eliminará permanentemente tu cuenta, estadísticas, skins y todo tu progreso. No se puede deshacer.",
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#ef4444',
                    confirmButtonText: 'Sí, eliminar mi cuenta',
                    cancelButtonText: 'Cancelar'
                });

                if (result.isConfirmed) {
                    try {
                        const res = await fetch("/api/account", {
                            method: "DELETE",
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        if (res.ok) {
                            try {
                                await this.authClient?.auth.signOut();
                            } catch {}

                            localStorage.removeItem("alchem_auth_token");
                            localStorage.removeItem("alchem_profile");
                            localStorage.removeItem("alchem_alias");
                            localStorage.removeItem("colyseus-auth-token");

                            await Swal.fire({
                                icon: 'success',
                                title: 'Cuenta eliminada',
                                text: 'Tu cuenta y todos tus datos asociados han sido eliminados de acuerdo a la Política de Privacidad.',
                                timer: 2500,
                                showConfirmButton: false,
                            });

                            window.location.reload();
                        } else {
                            Swal.fire('Error', 'No se pudo eliminar la cuenta. Inténtalo de nuevo.', 'error');
                        }
                    } catch (e) {
                        Swal.fire('Error', 'Error de red al eliminar la cuenta.', 'error');
                    }
                }
            });
        }
    }

    public async refreshProfile(): Promise<void> {
        const p = profileManager.getProfile();
        const storedAlias = safeGetStorage("alchem_alias") || "—";

        if (this.profileAliasValue) this.profileAliasValue.textContent = storedAlias;
        if (this.statGames) this.statGames.textContent = String(p.gamesPlayed);
        if (this.statTime) this.statTime.textContent = formatTime(p.totalTimeMs);
        if (this.statKills) this.statKills.textContent = String(p.totalKills);
        if (this.statBestMass) this.statBestMass.textContent = String(p.bestMass);
        if (this.statStreak) this.statStreak.textContent = String(p.currentStreak);
        if (this.statBestStreak) this.statBestStreak.textContent = String(p.bestStreak);
        if (this.statBestRank) this.statBestRank.textContent = p.bestRank > 0 ? `#${p.bestRank}` : "—";
        if (this.statFavorite) {
            const fav = profileManager.getFavoriteElement();
            this.statFavorite.textContent = fav ? fav.toUpperCase() : "—";
        }
        if (this.statCountry) {
            this.statCountry.textContent = p.country || "—";
        }

        // Si hay token, consultar estadísticas actualizadas de Supabase
        const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
        if (!token) return;

        void emoteManager.refreshStatus();

        try {
            const res = await fetch("/api/profile", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) return;

            const profile = await res.json();
            if (profile && profile.userId) {
                profileManager.hydrate(profile);
                if (profile.alias && this.profileAliasValue) {
                    this.profileAliasValue.textContent = profile.alias;
                }
                if (this.statGames) this.statGames.textContent = String(profile.totalGames ?? p.gamesPlayed);
                if (this.statTime) this.statTime.textContent = formatTime(profile.totalTimeMs ?? p.totalTimeMs);
                if (this.statKills) this.statKills.textContent = String(profile.totalKills ?? p.totalKills);
                if (this.statBestMass) this.statBestMass.textContent = String(profile.bestMass ?? p.bestMass);
                if (this.statStreak) this.statStreak.textContent = String(profile.currentStreak ?? p.currentStreak);
                if (this.statBestStreak) this.statBestStreak.textContent = String(profile.bestStreak ?? p.bestStreak);
                if (this.statBestRank) {
                    const rank = profile.bestRank ?? p.bestRank;
                    this.statBestRank.textContent = rank > 0 ? `#${rank}` : "—";
                }
                if (this.statCountry) {
                    this.statCountry.textContent = profile.country || p.country || "—";
                }
            }
        } catch (e) {
            console.warn("[Account] Error al actualizar perfil desde Supabase:", e);
        }
    }

    public async refreshLeaderboards(): Promise<void> {
        if (this.leaderboardLoading) this.leaderboardLoading.classList.remove("hidden");

        const data = await fetchGlobalLeaderboard("season");

        if (this.leaderboardLoading) this.leaderboardLoading.classList.add("hidden");

        if (data) {
            renderGlobalLeaderboardTable(this.killsTbody, data.topByKills || [], "totalKills");
            renderGlobalLeaderboardTable(this.massTbody, data.topByMass || [], "bestMass");
            renderGlobalLeaderboardTable(this.streakTbody, data.topByStreak || [], "bestStreak");
            renderGlobalLeaderboardTable(this.timeTbody, data.topByTime || [], "totalTimeMs");
        } else {
            if (this.killsTbody) {
                this.killsTbody.innerHTML = `<tr><td colspan="3" class="empty-cell">Error al conectar con el servidor</td></tr>`;
            }
            if (this.massTbody) {
                this.massTbody.innerHTML = `<tr><td colspan="3" class="empty-cell">Error al conectar con el servidor</td></tr>`;
            }
            if (this.streakTbody) {
                this.streakTbody.innerHTML = `<tr><td colspan="3" class="empty-cell">Error al conectar con el servidor</td></tr>`;
            }
            if (this.timeTbody) {
                this.timeTbody.innerHTML = `<tr><td colspan="3" class="empty-cell">Error al conectar con el servidor</td></tr>`;
            }
        }
    }

    private async handleOAuthLogin(provider: "google" | "facebook"): Promise<void> {
        if (!this.authClient) {
            this.showAccountFeedback("Servicio de autenticación no disponible.", "error");
            return;
        }

        const providerLabel = provider === "google" ? "Google" : "Facebook";

        try {
            // Colyseus abre una ventana popup para el OAuth
            const userdata = await this.authClient.auth.signInWithProvider(provider);
            console.log(`[OAuth] Login exitoso con ${providerLabel}:`, userdata);

            // Guardar el token y actualizar la UI
            if (userdata && (userdata as any).token) {
                localStorage.setItem("alchem_auth_token", (userdata as any).token);
            }
            await this.checkAuthState();

            // Cargar perfil del usuario desde Supabase
            const token = this.authClient.auth.token || localStorage.getItem("alchem_auth_token");
            if (token) {
                try {
                    const profileRes = await fetch("/api/profile", {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (profileRes.ok) {
                        const p = await profileRes.json();
                        if (p && p.userId) {
                            profileManager.hydrate(p);
                            if (p.alias) {
                                safeSetStorage("alchem_alias", p.alias);
                                const aliasInput = document.getElementById("alias-input") as HTMLInputElement | null;
                                if (aliasInput) aliasInput.value = p.alias;
                            }
                            const safeCos = p.cosmetics && Object.keys(p.cosmetics).length > 0 ? sanitizeCosmetics(p.cosmetics) : DEFAULT_COSMETICS;
                            localStorage.setItem("alchem_cosmetics", JSON.stringify(safeCos));
                            window.dispatchEvent(new CustomEvent("alchem:auth_change", { 
                                detail: { isLoggedIn: true, alias: p.alias || "Alquimista", profile: p, cosmetics: safeCos } 
                            }));
                            void questManager.fetchServerState();

                            if (p.welcomeBonusJustGranted) {
                                questManager.updateBalances(p.crystals, p.astralOrbs);
                                document.getElementById("welcome-bonus-banner")?.classList.add("hidden");
                                void Swal.fire({
                                    title: "🎉 ¡Bono de Bienvenida Acreditado!",
                                    text: "Por unirte a Alchem.io has recibido +200 💎 Cristales Alquímicos y +10 🔮 Piedras Filosofales en tu cuenta.",
                                    icon: "success",
                                    confirmButtonText: "¡Genial!",
                                    confirmButtonColor: "#f59e0b",
                                    background: "#0f172a",
                                    color: "#f8fafc",
                                });
                            }
                        }
                    }
                } catch (e) {
                    console.warn(`[OAuth] Error al cargar perfil tras autenticación con ${providerLabel}:`, e);
                }
            }

            await Swal.fire({
                icon: 'success',
                title: '¡Bienvenido!',
                text: `Has iniciado sesión con ${providerLabel}.`,
                timer: 2000,
                showConfirmButton: false,
            });

            // Cerrar automáticamente el modal tras iniciar sesión
            setTimeout(() => {
                this.close();
            }, 300);
        } catch (err: any) {
            console.error(`[OAuth] Error con ${providerLabel}:`, err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: err?.message || `No se pudo iniciar sesión con ${providerLabel}. Inténtalo de nuevo.`,
            });
        }
    }

    private initLeaderboardControls(): void {
        const seasonBtn = document.getElementById("leaderboard-mode-season");
        if (seasonBtn) {
            seasonBtn.addEventListener("click", () => {
                void this.refreshLeaderboards();
            });
        }
        if (this.leaderboardRefreshBtn) {
            this.leaderboardRefreshBtn.addEventListener("click", () => {
                void this.refreshLeaderboards();
            });
        }
    }

    private initModalControls(): void {
        if (this.accountBtn) {
            this.accountBtn.addEventListener("click", () => {
                const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                if (token && token.trim() !== "" && token !== "guest_demo_token") {
                    window.location.href = "/perfil.html";
                } else {
                    this.toggle();
                }
            });
        }

        const aliasAccountBtn = document.getElementById("open-account-btn") || document.getElementById("alias-account-btn");
        if (aliasAccountBtn) {
            aliasAccountBtn.addEventListener("click", () => {
                const token = safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token");
                if (token && token.trim() !== "" && token !== "guest_demo_token") {
                    window.location.href = "/perfil.html";
                } else {
                    this.open();
                }
            });
        }

        if (this.accountClose) {
            this.accountClose.addEventListener("click", () => this.close());
        }

        if (this.accountModal) {
            this.accountModal.addEventListener("click", (e) => {
                if (e.target === this.accountModal && performance.now() - this.modalOpenedAt > 150) {
                    this.close();
                }
            });
        }
    }
}

/**
 * Solicita un token fresco de Google reCAPTCHA v3 invisible para la acción indicada.
 * El token expira en ~2 minutos según el protocolo de Google reCAPTCHA.
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
    return new Promise((resolve) => {
        if (typeof (window as any).grecaptcha === "undefined") {
            console.warn("[reCAPTCHA] No cargado o bloqueado en el navegador. Saltando verificación.");
            resolve(null);
            return;
        }
        (window as any).grecaptcha.ready(() => {
            const siteKey = (window as any).RECAPTCHA_SITE_KEY || "TU_SITE_KEY_AQUI";
            try {
                (window as any).grecaptcha.execute(siteKey, { action }).then((token: string) => {
                    resolve(token);
                }).catch((err: any) => {
                    console.warn("[reCAPTCHA] Error al ejecutar execute:", err);
                    resolve(null);
                });
            } catch (err) {
                console.warn("[reCAPTCHA] Excepción al invocar execute:", err);
                resolve(null);
            }
        });
    });
}
